import { useCallback, useEffect, useRef, useState } from "react";

export function advanceIndex(
  current: number,
  direction: 1 | -1,
  length: number,
): number {
  if (length <= 0) return 0;
  return (current + direction + length) % length;
}

export function useMenuPresenter() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) =>
      event.key === "Escape" && setOpen(false);
    document.body.classList.add("menu-open");
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.classList.remove("menu-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);
  return {
    open,
    toggle: () => setOpen((value) => !value),
    close: () => setOpen(false),
  };
}

type CarouselOptions = {
  autoPlayMs?: number;
};

export function createLoopOrder(itemCount: number): number[] {
  if (itemCount <= 0) return [];
  const indexes = Array.from({ length: itemCount }, (_, index) => index);
  return [indexes[indexes.length - 1], ...indexes.slice(0, -1)];
}

export function rotateLoopOrder(order: number[], direction: 1 | -1): number[] {
  if (order.length <= 1) return order;
  return direction === 1
    ? [...order.slice(1), order[0]]
    : [order[order.length - 1], ...order.slice(0, -1)];
}

export function useCarouselPresenter(
  length: number,
  { autoPlayMs = 0 }: CarouselOptions = {},
) {
  const [index, setIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);
  const [userPaused, setUserPaused] = useState(false);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const indexRef = useRef(0);
  const transitionTo = useCallback(
    (target: number) => {
      if (length <= 0) return;
      const normalized = ((target % length) + length) % length;
      if (normalized === indexRef.current) return;
      setPreviousIndex(indexRef.current);
      indexRef.current = normalized;
      setIndex(normalized);
    },
    [length],
  );
  const next = useCallback(
    () => transitionTo(advanceIndex(indexRef.current, 1, length)),
    [length, transitionTo],
  );
  const previous = useCallback(
    () => transitionTo(advanceIndex(indexRef.current, -1, length)),
    [length, transitionTo],
  );

  useEffect(() => {
    if (previousIndex === null) return;
    const timer = window.setTimeout(() => setPreviousIndex(null), 820);
    return () => window.clearTimeout(timer);
  }, [index, previousIndex]);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(query.matches);
    updatePreference();
    query.addEventListener("change", updatePreference);
    return () => query.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    if (
      autoPlayMs <= 0 ||
      length <= 1 ||
      userPaused ||
      interactionPaused ||
      reducedMotion
    )
      return;
    const timer = window.setTimeout(next, autoPlayMs);
    return () => window.clearTimeout(timer);
  }, [
    autoPlayMs,
    index,
    interactionPaused,
    length,
    next,
    reducedMotion,
    userPaused,
  ]);

  const isAutoPlaying =
    autoPlayMs > 0 &&
    length > 1 &&
    !userPaused &&
    !interactionPaused &&
    !reducedMotion;

  return {
    index,
    previousIndex,
    next,
    previous,
    goTo: transitionTo,
    isAutoPlaying,
    userPaused,
    pauseForInteraction: () => setInteractionPaused(true),
    resumeAfterInteraction: () => setInteractionPaused(false),
    toggleAutoPlay: () => setUserPaused((paused) => !paused),
  };
}

export function useHorizontalSliderPresenter(
  itemCount: number,
  { autoPlayMs = 0 }: CarouselOptions = {},
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const stepRef = useRef(0);
  const dragStartRef = useRef<number | null>(null);
  const dragDeltaRef = useRef(0);
  const draggedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const directionRef = useRef<1 | -1 | 0>(0);
  const animatingRef = useRef(false);
  const animationTimerRef = useRef<number | null>(null);
  const [orderedIndexes, setOrderedIndexes] = useState(() =>
    createLoopOrder(itemCount),
  );
  const [offset, setOffset] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(query.matches);
    updatePreference();
    query.addEventListener("change", updatePreference);
    return () => query.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const measure = () => {
      const firstSlide = track.querySelector<HTMLElement>("[data-event-slide]");
      if (!firstSlide) return;
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = firstSlide.getBoundingClientRect().width + gap;
      stepRef.current = step;
      if (!animatingRef.current && dragStartRef.current === null)
        setOffset(-step);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [itemCount]);

  const clearAnimationTimer = useCallback(() => {
    if (animationTimerRef.current === null) return;
    window.clearTimeout(animationTimerRef.current);
    animationTimerRef.current = null;
  }, []);

  const completeAnimation = useCallback(() => {
    if (!animatingRef.current) return;
    clearAnimationTimer();
    const direction = directionRef.current;
    if (direction !== 0)
      setOrderedIndexes((current) => rotateLoopOrder(current, direction));
    directionRef.current = 0;
    animatingRef.current = false;
    setIsAnimating(false);
    setOffset(-stepRef.current);
  }, [clearAnimationTimer]);

  const settleImmediately = useCallback(
    (direction: 1 | -1 | 0 = 0) => {
      clearAnimationTimer();
      if (direction !== 0)
        setOrderedIndexes((current) => rotateLoopOrder(current, direction));
      directionRef.current = 0;
      animatingRef.current = false;
      setIsAnimating(false);
      setOffset(-stepRef.current);
    },
    [clearAnimationTimer],
  );

  const animateTo = useCallback(
    (targetOffset: number, direction: 1 | -1 | 0) => {
      clearAnimationTimer();
      directionRef.current = direction;
      animatingRef.current = true;
      setIsAnimating(true);
      setOffset(targetOffset);
      // Browsers do not emit transitionend when the dragged transform already
      // equals the target. Always release the animation lock as a safeguard.
      animationTimerRef.current = window.setTimeout(completeAnimation, 800);
    },
    [clearAnimationTimer, completeAnimation],
  );

  const move = useCallback(
    (direction: 1 | -1) => {
      const step = stepRef.current;
      if (itemCount <= 1 || step <= 0 || animatingRef.current) return;
      if (reducedMotion) {
        settleImmediately(direction);
        return;
      }
      animateTo(direction === 1 ? -step * 2 : 0, direction);
    },
    [animateTo, itemCount, reducedMotion, settleImmediately],
  );

  const next = useCallback(() => move(1), [move]);
  const previous = useCallback(() => move(-1), [move]);

  const pointerDown = useCallback((clientX: number) => {
    if (animatingRef.current) return;
    dragStartRef.current = clientX;
    dragDeltaRef.current = 0;
    draggedRef.current = false;
    setIsAnimating(false);
    document.body.classList.add("carousel-dragging");
  }, []);

  const pointerMove = useCallback((clientX: number) => {
    if (dragStartRef.current === null || animatingRef.current) return;
    const step = stepRef.current;
    const delta = Math.max(
      -step,
      Math.min(step, clientX - dragStartRef.current),
    );
    dragDeltaRef.current = delta;
    if (Math.abs(delta) > 5) draggedRef.current = true;
    setOffset(-step + delta);
  }, []);

  const finishDrag = useCallback(() => {
    if (dragStartRef.current === null) return false;
    const step = stepRef.current;
    const delta = dragDeltaRef.current;
    const dragged = draggedRef.current;
    dragStartRef.current = null;
    dragDeltaRef.current = 0;
    suppressClickRef.current = dragged;
    document.body.classList.remove("carousel-dragging");

    const threshold = Math.min(80, step * 0.16);
    if (Math.abs(delta) < threshold) {
      if (!dragged || reducedMotion || Math.abs(delta) <= 1)
        settleImmediately();
      else animateTo(-step, 0);
      return dragged;
    }

    const direction = delta < 0 ? 1 : -1;
    // At a full-card drag the track is already at its final transform, so
    // recycle the card immediately instead of waiting for a nonexistent event.
    if (Math.abs(delta) >= step - 1 || reducedMotion)
      settleImmediately(direction);
    else move(direction);
    return dragged;
  }, [animateTo, move, reducedMotion, settleImmediately]);

  const cancelDrag = useCallback(() => {
    if (dragStartRef.current === null) return;
    const delta = dragDeltaRef.current;
    dragStartRef.current = null;
    dragDeltaRef.current = 0;
    document.body.classList.remove("carousel-dragging");
    if (Math.abs(delta) <= 1 || reducedMotion) {
      settleImmediately();
      return;
    }
    animateTo(-stepRef.current, 0);
  }, [animateTo, reducedMotion, settleImmediately]);

  const shouldSuppressClick = useCallback(() => {
    const suppress = suppressClickRef.current;
    suppressClickRef.current = false;
    return suppress;
  }, []);

  useEffect(() => {
    if (
      autoPlayMs <= 0 ||
      itemCount <= 1 ||
      userPaused ||
      interactionPaused ||
      reducedMotion
    )
      return;
    const timer = window.setInterval(next, autoPlayMs);
    return () => window.clearInterval(timer);
  }, [
    autoPlayMs,
    interactionPaused,
    itemCount,
    next,
    reducedMotion,
    userPaused,
  ]);

  useEffect(
    () => () => {
      clearAnimationTimer();
      document.body.classList.remove("carousel-dragging");
    },
    [clearAnimationTimer],
  );

  return {
    viewportRef,
    trackRef,
    orderedIndexes,
    trackStyle:
      offset === null
        ? undefined
        : { transform: `translate3d(${offset}px, 0, 0)` },
    isAnimating,
    next,
    previous,
    onTransitionEnd: completeAnimation,
    pointerDown,
    pointerMove,
    finishDrag,
    cancelDrag,
    shouldSuppressClick,
    userPaused,
    pauseForInteraction: () => setInteractionPaused(true),
    resumeAfterInteraction: () => setInteractionPaused(false),
    toggleAutoPlay: () => setUserPaused((paused) => !paused),
  };
}

export function useLightboxPresenter(length: number) {
  const [index, setIndex] = useState<number | null>(null);
  const close = useCallback(() => setIndex(null), []);
  const next = useCallback(
    () =>
      setIndex((current) =>
        current === null ? null : advanceIndex(current, 1, length),
      ),
    [length],
  );
  const previous = useCallback(
    () =>
      setIndex((current) =>
        current === null ? null : advanceIndex(current, -1, length),
      ),
    [length],
  );
  useEffect(() => {
    if (index === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") previous();
    };
    document.body.classList.add("modal-open");
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [close, index, next, previous]);
  return { index, open: setIndex, close, next, previous };
}
